<template>
  <v-sheet min-height="400px">
    <div class="mx-6" ref="chart"></div>
  </v-sheet>
</template>

<script>
import Highcharts from 'highcharts'
import Exporting from 'highcharts/modules/exporting'
import ExportData from 'highcharts/modules/export-data'

Exporting(Highcharts)
ExportData(Highcharts)

export default {
  name: 'MetaScatterPlot',

  props: {
    data: Array,
    type: {
      type: String,
      default: 'kinase'
    }
  },

  mounted() {
    this.renderChart()
  },

  watch: {
    data: {
      deep: true,
      handler() {
        this.renderChart()
      }
    }
  },

  methods: {

    // signed -log10
    signedLog10(val) {
      if (val === null || val === undefined || val === '') return null
      const num = Number(val)
      if (!isFinite(num) || num === 0) return null
      return Math.sign(num) * (-Math.log10(Math.abs(num)))
    },

    renderChart() {

      const yKey = this.type === 'kinase'
          ? 'kinase_metap'
          : 'phosphatase_metap'

      const labelKey = this.type === 'kinase'
          ? 'kinase'
          : 'phosphatase'

      const yTitle = this.type === 'kinase'
          ? 'Signed -log10(Meta-p of Site-Kinase Association)'
          : 'Signed -log10(Meta-p of Site-Phosphatase Association)'

      const mainColor = this.type === 'kinase'
          ? '#1f77b4'
          : '#ff7f0e'

      const allPoints = []

      this.data.forEach(d => {
        const x = this.signedLog10(d.cis_protein_metap)
        const y = this.signedLog10(d[yKey])
        if (x === null || y === null) return

        allPoints.push({
          x, y,
          id: d.id,
          internal_id: d.internal_id,
          ptm_type: d.ptm_type || 'phosphorylation',
          labelName: d[labelKey],
          raw: d
        })
      })

      // ========= label =========
      const labelPoints = allPoints
          .filter(p => {
            const absX = Math.abs(p.x)
            const absY = Math.abs(p.y)
            return absY >= 2 * absX && absY > 2
          })
          .sort((a, b) => Math.abs(b.y) - Math.abs(a.y))
          .slice(0, 10)

      const labelSet = new Set(labelPoints.map(p => p.internal_id))

      const xs = allPoints.map(p => p.x)
      const ys = allPoints.map(p => p.y)

      const xMin = xs.length ? Math.min(...xs) : -10
      const xMax = xs.length ? Math.max(...xs) : 10
      const yMin = ys.length ? Math.min(...ys) : -10
      const yMax = ys.length ? Math.max(...ys) : 10

      const maxAbs = Math.max(
          Math.abs(xMin), Math.abs(xMax),
          Math.abs(yMin), Math.abs(yMax)
      )

      const L = maxAbs * 1.05

      let lineData

      if (this.type === 'kinase') {
        lineData = [[-L, -L], [L, L]]
      } else {
        lineData = [[-L, L], [L, -L]]
      }

      Highcharts.chart(this.$refs.chart, {

        chart: {
          type: 'scatter',
          zoomType: 'xy',
          spacingTop: 30
        },

        title: {text: null},
        legend: {enabled: false},

        // ========= X =========
        xAxis: {
          title: {
            text: 'Signed -log10(Meta-p of Site-Protein Association)'
          },
          min: Math.floor(xMin * 1.05),
          max: Math.ceil(xMax * 1.05),

          plotLines: [
            {value: 0, width: 1, color: '#999'},

            {
              value: 2,
              width: 1,
              color: '#666',
              dashStyle: 'ShortDash',
            }
          ]
        },

        // ========= Y =========
        yAxis: {
          title: {text: yTitle},
          min: Math.floor(yMin * 1.05),
          max: Math.ceil(yMax * 1.05),
          lineWidth: 1,
          lineColor: '#333',
          tickLength: 5,
          tickWidth: 1,
          tickColor: '#333',
          plotLines: [
            {value: 0, width: 2, color: '#000'},

            // y = 2
            {
              value: 2,
              width: 1,
              color: '#666',
              dashStyle: 'ShortDash',
            },

            // y = -2（ phosphatase）
            ...(this.type === 'phosphatase'
                ? [{
                  value: -2,
                  width: 1,
                  color: '#666',
                  dashStyle: 'ShortDash',

                }]
                : [])
          ]
        },

        // ========= tooltip =========
        tooltip: {
          useHTML: true,
          formatter: function () {
            const p = this.point
            const isKinase = p.series.userOptions.customType === 'kinase'

            const regulator = isKinase ? 'kinase' : 'phosphatase'
            const meta = isKinase
                ? p.raw.kinase_metap
                : p.raw.phosphatase_metap

            return `
              <b>${p.id}</b><br/>
              site-protein correlation: ${p.raw.cis_protein_metap}<br/>
              most significant ${regulator}: ${p.labelName}<br/>
              site-${regulator} correlation: ${meta}
            `
          }
        },

        // ========= click =========
        plotOptions: {
          series: {
            point: {
              events: {
                click: function () {
                  const base_url = window.location.origin
                  const url = `${base_url}/#/single-search/${this.internal_id}?ptm_type=${encodeURIComponent(this.ptm_type)}`
                  window.open(url, '_blank')
                }
              }
            }
          }
        },

        // ========= data =========
        series: [

          {
            type: 'scatter',
            data: allPoints.map(p => ({
              ...p,
              marker: {
                symbol: labelSet.has(p.internal_id) ? 'diamond' : 'circle',
                radius: labelSet.has(p.internal_id) ? 4 : 3
              }
            })),
            color: mainColor,
            customType: this.type
          },

          // label
          {
            type: 'scatter',
            data: labelPoints,
            color: 'transparent',
            enableMouseTracking: false,
            dataLabels: {
              enabled: true,
              allowOverlap: false,
              formatter: function () {
                return `${this.point.id} | ${this.point.labelName}`
              },
              x: 8,
              y: -5,
              style: {fontSize: '10px'}
            }
          },

          {
            type: 'line',
            data: lineData,
            color: 'red',
            dashStyle: 'Dash',
            lineWidth: 1,
            marker: {enabled: false},
            enableMouseTracking: false
          }
        ],

        // ========= export  =========
        exporting: {
          enabled: true,
          buttons: {
            contextButton: {
              menuItems: ['downloadPNG', 'downloadSVG']
            }
          }
        }

      })
    }
  }
}
</script>